import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";
import {
  Users,
  Target,
  Brain,
  Award,
  Zap,
  CheckCircle,
  ArrowRight,
  Quote,
  Star,
  TrendingUp,
  Shield,
  Clock,
} from "lucide-react";

const AboutUsPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const waitlistUrl = "https://portal.selfsync.ai/sign-up";

  // Organization schema markup
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "SelfSync",
    alternateName: "SelfSync.ai",
    description:
      "AI-powered hiring assessment platform that predicts candidate success with 99% accuracy using advanced personality psychology.",
    url: "https://selfsync.ai",
    logo: "https://selfsync.ai/static/media/SelfSync%20Logo.df7570a5489657df44eb.png",
    foundingDate: "2024",
    founder: {
      "@type": "Organization",
      name: "Digikore Studios Limited",
    },
    industry: "Human Resources Technology",
    numberOfEmployees: "11-50",
    address: {
      "@type": "PostalAddress",
      addressCountry: "IN",
    },
    contactPoint: {
      "@type": "ContactPoint",
      telephone: "+1-408-555-0100",
      contactType: "customer service",
      email: "support@selfsync.ai",
      availableLanguage: "en",
    },
    sameAs: ["https://selfsync.ai"],
  };

  const stats = [
    {
      number: "99%",
      label: "Hiring Accuracy",
      icon: <Target className="w-8 h-8" />,
    },
    {
      number: "260+",
      label: "Companies Trust Us",
      icon: <Users className="w-8 h-8" />,
    },
    {
      number: "30 min",
      label: "Assessment Time",
      icon: <Clock className="w-8 h-8" />,
    },
    {
      number: "60K+",
      label: "Candidates Assessed",
      icon: <Brain className="w-8 h-8" />,
    },
  ];

  const values = [
    {
      icon: <Brain className="w-12 h-12 text-purple-600" />,
      title: "Science-Driven",
      description:
        "Every decision backed by psychology research and AI precision",
    },
    {
      icon: <Shield className="w-12 h-12 text-cyan-500" />,
      title: "Bias-Free",
      description:
        "Removing human bias to create fair, objective hiring processes",
    },
    {
      icon: <Zap className="w-12 h-12 text-green-500" />,
      title: "Instant Results",
      description:
        "Get deep insights in minutes, not weeks of lengthy processes",
    },
    {
      icon: <TrendingUp className="w-12 h-12 text-orange-500" />,
      title: "Proven ROI",
      description: "3x better retention and 25% higher productivity guaranteed",
    },
  ];

  const timeline = [
    {
      year: "Q1 2024",
      title: "Foundation",
      description:
        "SelfSync launched by Digikore Studios to revolutionize hiring with AI psychology",
    },
    {
      year: "Q2 2024",
      title: "First 100 Companies",
      description:
        "Reached our first milestone with 99% accuracy rate validation",
    },
    {
      year: "Q3 2024",
      title: "AI Enhancement",
      description:
        "Advanced our AI model to predict role-specific success patterns",
    },
    {
      year: "Q4 2024",
      title: "260+ Companies",
      description:
        "Growing community of forward-thinking companies transforming their hiring",
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>
          About SelfSync: The Story Behind 99% Accurate AI Hiring | Meet Our
          Team
        </title>
        <meta
          name="description"
          content="Discover how SelfSync revolutionizes hiring with 99% accuracy using AI psychology. Meet the team behind the platform trusted by 260+ companies to eliminate bad hires."
        />
        <meta
          name="keywords"
          content="about selfsync, AI hiring platform, psychology-based recruitment, hiring accuracy, team behind selfsync"
        />
        <link rel="canonical" href="https://selfsync.ai/about" />

        {/* Open Graph tags */}
        <meta
          property="og:title"
          content="About SelfSync: The Story Behind 99% Accurate AI Hiring"
        />
        <meta
          property="og:description"
          content="Discover how SelfSync revolutionizes hiring with AI psychology. Meet the team transforming recruitment for 260+ companies."
        />
        <meta property="og:url" content="https://selfsync.ai/about" />
        <meta property="og:type" content="website" />

        {/* Schema markup */}
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-purple-900 via-purple-800 to-cyan-800 text-white py-20">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-14 lg:px-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-5xl md:text-6xl font-bold mb-6">
                We're Fixing
                <span className="bg-gradient-to-r from-cyan-400 to-purple-400 text-transparent bg-clip-text">
                  {" "}
                  Broken Hiring
                </span>
              </h1>
              <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed">
                Every bad hire costs companies $50,000+. We use AI psychology to
                predict success with 99% accuracy, so you never waste time on
                wrong candidates again.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <motion.button
                  className="bg-gradient-to-r from-cyan-500 to-purple-600 text-white font-bold px-8 py-2 rounded-lg hover:from-cyan-600 hover:to-purple-700 transition-all shadow-lg text-lg"
                  onClick={() => window.open(waitlistUrl, "_blank")}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Try Free Assessment
                </motion.button>
                <motion.button
                  className="border-2 border-white/30 text-white font-bold px-8 py-2 rounded-lg hover:bg-white/10 transition-all text-lg"
                  onClick={() =>
                    document
                      .getElementById("story")
                      .scrollIntoView({ behavior: "smooth" })
                  }
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Read Our Story
                </motion.button>
              </div>
            </motion.div>

            <motion.div
              className="relative"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                  alt="SelfSync team collaborating"
                  className="w-full h-96 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                <div className="absolute bottom-6 left-6 right-6">
                  <p className="text-white font-medium text-lg">
                    "We believe every hiring decision should be backed by
                    science, not gut feelings."
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-8 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 md:px-14 lg:px-20">
          <div className="grid  grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                className="text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                viewport={{ once: true }}
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-purple-600 to-cyan-500 text-white rounded-full mb-4">
                  {stat.icon}
                </div>
                <div className="text-4xl font-bold text-gray-900 mb-2">
                  {stat.number}
                </div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section id="story" className="py-12">
        <div className="max-w-7xl mx-auto px-6 md:px-14 lg:px-20">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-5xl font-bold mb-8">
                The Problem That
                <span className="bg-gradient-to-r from-purple-600 to-cyan-500 text-transparent bg-clip-text">
                  {" "}
                  Inspired Us
                </span>
              </h2>

              <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
                <p>
                  <strong className="text-gray-900">
                    74% of companies admit they've hired the wrong person.
                  </strong>{" "}
                  The cost? $50,000+ per bad hire in wasted salary, training,
                  and lost productivity.
                </p>

                <p>
                  Traditional hiring relies on gut feelings, biased interviews,
                  and resume screening that misses the most important factor:{" "}
                  <strong className="text-purple-600">personality fit</strong>.
                </p>

                <p>
                  As part of Digikore Studios Limited, we've seen countless
                  companies struggle with this problem. That's why we built
                  SelfSync - an AI platform that reads personality patterns like
                  a psychologist, predicting job success with 99% accuracy.
                </p>

                <p>
                  <strong className="text-gray-900">
                    Our mission is simple:
                  </strong>{" "}
                  Eliminate bad hires forever by making psychology-based hiring
                  accessible to every company.
                </p>
              </div>

              <div className="mt-8 p-6 bg-gradient-to-r from-purple-50 to-cyan-50 rounded-xl border border-purple-100">
                <Quote className="w-8 h-8 text-purple-600 mb-4" />
                <p className="text-lg italic text-gray-800 mb-4">
                  "We don't just help you hire better people. We help you build
                  better teams, reduce turnover, and create workplaces where the
                  right people thrive."
                </p>
                <div className="text-sm font-medium text-purple-600">
                  — SelfSync Team
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img
                src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                alt="Team analyzing hiring data"
                className="rounded-2xl shadow-2xl w-full"
              />

              {/* Floating Success Card */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl p-3 md:p-6 shadow-xl border border-gray-100">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-gray-900">99%</div>
                    <div className="text-sm text-gray-600">Success Rate</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Values Section */}
      <section className="py-10 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 md:px-14 lg:px-20">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              What Drives
              <span className="bg-gradient-to-r from-purple-600 to-cyan-500 text-transparent bg-clip-text">
                {" "}
                Everything We Do
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our core values shape every feature, every algorithm, and every
              interaction
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 text-center group"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                viewport={{ once: true }}
              >
                <div className="mb-6 flex justify-center">
                  <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    {value.icon}
                  </div>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  {value.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Company Timeline */}
      <section className="py-10">
        <div className="max-w-7xl mx-auto px-6 md:px-14 lg:px-20">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Our
              <span className="bg-gradient-to-r from-purple-600 to-cyan-500 text-transparent bg-clip-text">
                {" "}
                Journey
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From concept to revolutionizing hiring for 260+ companies
            </p>
          </motion.div>

          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-1/2 transform -translate-x-0.5 h-full w-0.5 bg-gradient-to-b from-purple-500 to-cyan-500"></div>

            <div className="">
              {timeline.map((item, index) => (
                <motion.div
                  key={index}
                  className={`flex items-center ${
                    index % 2 === 0 ? "flex-row" : "flex-row-reverse"
                  }`}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.2, duration: 0.6 }}
                  viewport={{ once: true }}
                >
                  <div
                    className={`w-1/2 ${
                      index % 2 === 0
                        ? "md:pr-4 text-right"
                        : "md:pl-4 text-left"
                    }`}
                  >
                    <div className="bg-white rounded-xl p-6 max-md:p-3 shadow-lg border border-gray-100">
                      <div className="text-2xl md:text-3xl font-bold text-purple-600 mb-2">
                        {item.year}
                      </div>
                      <h3 className="text-base md:text-xl font-bold text-gray-900 mb-3">
                        {item.title}
                      </h3>
                      <p className="text-gray-600 text-sm text-start">
                        {item.description}
                      </p>
                    </div>
                  </div>

                  {/* Timeline dot */}
                  <div className="relative z-10">
                    <div className="w-4 h-4 bg-gradient-to-r from-purple-500 to-cyan-500 rounded-full border-4 border-white shadow-lg"></div>
                  </div>

                  <div className="w-1/2"></div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutUsPage;
